import { useI18n } from '@affine/i18n';
import { RightSidebarIcon } from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { useLayoutEffect, useMemo } from 'react';
import { combineLatest } from 'rxjs';

import { ViewService } from '../../workbench/services/view';
import { WorkbenchService } from '../../workbench/services/workbench';
import {
  EMPTY_NAVIGATION_ARGS,
  navigationActionId,
  NavigationActivationError,
  type UiNavigationDefinition,
} from './workspace-navigation-catalog';
import { WorkspaceViewContextService } from './workspace-view-context';

export const RIGHT_SIDEBAR_NAVIGATION_OWNER = 'right-sidebar';
export const RIGHT_SIDEBAR_OPEN_ID = 'open';
export const rightSidebarTabDefinitionId = (id: string) =>
  JSON.stringify(['tab', id]);

/** The normal View tab owner stays mounted when its separate sidebar target is closed. */
export function RightSidebarNavigationOwner() {
  const context = useService(WorkspaceViewContextService);
  const view = useService(ViewService).view;
  const workbench = useService(WorkbenchService).workbench;
  const tabs = useLiveData(view.sidebarTabs$);
  const t = useI18n();
  const label = t['com.streamlain.cmdk.affine.right-sidebar.expand']();
  const scope = useMemo(
    () => ({ workspaceId: context.workspaceId, viewId: view.id }),
    [context, view]
  );
  const items$ = useMemo(
    () => new LiveData<readonly UiNavigationDefinition[]>([]),
    []
  );
  const items = useMemo(() => {
    const current = () => {
      if (!context.isActiveScope(scope))
        throw new NavigationActivationError('stale');
    };
    const target = (entryId: string) => {
      const value = context.snapshot(scope);
      if (!value.ok || !workbench.sidebarOpen$.value) return null;
      return {
        ...scope,
        revision: value.snapshot.revision,
        surfaceId: value.snapshot.surfaceId,
        route: value.snapshot.route,
        title: value.snapshot.title,
        sidebar: { side: 'right' as const, entryId },
      };
    };
    const opener: UiNavigationDefinition = {
      id: RIGHT_SIDEBAR_OPEN_ID,
      label,
      icon: <RightSidebarIcon />,
      keywords: [],
      parentIds: [],
      argsSchema: EMPTY_NAVIGATION_ARGS,
      available: () => true,
      activate: () => {
        current();
        workbench.openSidebar();
      },
      readTarget: () => target(RIGHT_SIDEBAR_OPEN_ID),
    };
    return [
      opener,
      ...tabs.map(
        (tab): UiNavigationDefinition => ({
          id: rightSidebarTabDefinitionId(tab.id),
          label: tab.label ?? tab.id,
          icon: tab.icon,
          section: label,
          keywords: [],
          parentIds: [
            navigationActionId(
              scope,
              RIGHT_SIDEBAR_NAVIGATION_OWNER,
              opener.id
            ),
          ],
          argsSchema: EMPTY_NAVIGATION_ARGS,
          available: () =>
            view.sidebarTabs$.value.some(item => item.id === tab.id),
          activate: () => {
            current();
            if (!view.sidebarTabs$.value.some(item => item.id === tab.id))
              throw new NavigationActivationError('blocked');
            workbench.openSidebar();
            view.activeSidebarTab(tab.id);
          },
          readTarget: () =>
            view.activeSidebarTab$.value?.id === tab.id ? target(tab.id) : null,
        })
      ),
    ];
  }, [context, label, scope, tabs, view, workbench]);
  useLayoutEffect(
    () =>
      context.navigation.registerSource({
        scope,
        ownerId: RIGHT_SIDEBAR_NAVIGATION_OWNER,
        items$,
      }),
    [context, items$, scope]
  );
  useLayoutEffect(() => items$.next(items), [items, items$]);
  useLayoutEffect(() => {
    const subscription = combineLatest([
      view.activeSidebarTab$,
      workbench.sidebarOpen$,
    ]).subscribe(() => context.invalidate(scope, 'workbench'));
    return () => subscription.unsubscribe();
  }, [context, scope, view, workbench]);
  return null;
}
